import { useState, type FormEvent } from 'react';
import { parties, sourcing } from '../../api/endpoints';
import type {
    FulfillmentOption, Party, RequestItem, SupplierQuote, SupplierRequest, TechnicalCase,
} from '../../api/types';
import {
    Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, fmtMoney, JsonBlock,
    shortId,
    StatusBadge,
} from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'];

interface CalcData {
  cases: TechnicalCase[];
  supplierRequests: SupplierRequest[];
  quotes: SupplierQuote[];
}

function useCalcData(options: FulfillmentOption[], tick: number) {
  return useAsync<CalcData>(async () => {
    const caseLists = await Promise.all(
      options.map((o) => sourcing.technicalCases({ option: o.id }).catch(() => [] as TechnicalCase[])));
    const cases = caseLists.flat();

    const srLists = await Promise.all(
      options.map((o) => sourcing.supplierRequests({ option: o.id }).catch(() => [] as SupplierRequest[])));
    const supplierRequests = srLists.flat();

    const quoteLists = await Promise.all(
      supplierRequests.map((sr) => sourcing.quotes({ supplier_request: sr.id }).catch(() => [] as SupplierQuote[])));
    const visibleQuotes = await sourcing.quotes().catch(() => [] as SupplierQuote[]);
    const optionIds = new Set(options.map((o) => o.id));
    const quotesById = new Map<string, SupplierQuote>();
    for (const quote of [...quoteLists.flat(), ...visibleQuotes.filter((q) => !q.option_id || optionIds.has(q.option_id))]) {
      quotesById.set(quote.id, quote);
    }

    return { cases, supplierRequests, quotes: [...quotesById.values()] };
  }, [options.map((o) => o.id).join(','), tick]);
}

function CreateSupplierRequestForm({ option, onDone }: { option: FulfillmentOption; onDone: () => void }) {
  const partiesList = useAsync<Party[]>(() => parties.list(), []);
  const [supplier, setSupplier] = useState('');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await sourcing.createSupplierRequest({ option: option.id, supplier, option_revision: option.revision, external_number: subject });
      setSupplier(''); setSubject('');
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  return (
    <form className="inline-form" onSubmit={submit}>
      <ErrorBox error={err} />
      <div className="form-row">
        <Field label="Поставщик">
          <select value={supplier} onChange={(e) => setSupplier(e.target.value)} required>
            <option value="">— выберите —</option>
            {(partiesList.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Тема запроса"><input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="RQ по варианту исполнения" /></Field>
      </div>
      <div className="form-actions">
        <button className="btn primary" disabled={busy || !supplier}>{busy ? 'Создание…' : 'Создать запрос поставщику'}</button>
      </div>
    </form>
  );
}

function CreateQuoteForm({ sr, onDone }: { sr: SupplierRequest; onDone: () => void }) {
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('RUB');
  const [duration, setDuration] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await sourcing.createQuote({
        supplier_request: sr.id,
        request_revision: sr.revision,
        cost: cost || '0',
        currency,
        duration: duration ? Number(duration) : null,
        valid_until: validUntil ? new Date(validUntil).toISOString() : null,
      });
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  return (
    <form className="inline-form" onSubmit={submit}>
      <ErrorBox error={err} />
      <div className="form-row">
        <Field label="Стоимость"><input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} required /></Field>
        <Field label="Валюта">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Срок (дней)"><input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} /></Field>
        <Field label="Действительна до"><input type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
      </div>
      <div className="form-actions">
        <button className="btn primary" disabled={busy || !cost}>{busy ? 'Сохранение…' : 'Зарегистрировать котировку'}</button>
      </div>
    </form>
  );
}

function ActionButton({ title, run, onDone, confirm }: {
  title: string;
  run: () => Promise<unknown>;
  onDone: () => void;
  confirm?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const click = async () => {
    if (confirm && !window.confirm(confirm)) return;
    setBusy(true); setErr(null);
    try { await run(); onDone(); } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <button className="btn small" onClick={click} disabled={busy}>{title}</button>
      {err ? <span className="muted" style={{ fontSize: 12 }}>{err instanceof Error ? err.message : 'ошибка'}</span> : null}
    </span>
  );
}

function RegisterResponseButton({ sr, onDone }: { sr: SupplierRequest; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const click = async () => {
    const note = window.prompt('Зарегистрировать ответ поставщика. Комментарий / выдержка:', 'Ответ получен вручную');
    if (note === null) return;
    setBusy(true); setErr(null);
    try {
      await sourcing.supplierRequestAction(sr.id, 'register-response', {
        revision: sr.revision,
        received_at: new Date().toISOString(),
        source_type: 'manual',
        note,
      });
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <button className="btn small" onClick={click} disabled={busy}>Ответ получен</button>
      {err ? <span className="muted" style={{ fontSize: 12 }}>{err instanceof Error ? err.message : 'ошибка'}</span> : null}
    </span>
  );
}

function OptionCalcBlock({ option, item, calc, onChanged }: {
  option: FulfillmentOption;
  item?: RequestItem;
  calc: CalcData;
  onChanged: () => void;
}) {
  const [showSRForm, setShowSRForm] = useState(false);
  const [quoteFormFor, setQuoteFormFor] = useState<string | null>(null);
  const optionSRs = calc.supplierRequests.filter((sr) => sr.option === option.id);
  const optionCases = calc.cases.filter((c) => c.option === option.id);
  const srIds = new Set(optionSRs.map((s) => s.id));
  const optionQuotes = calc.quotes.filter((q) => srIds.has(q.supplier_request ?? '') || q.option_id === option.id);

  const caseCols: Column<TechnicalCase>[] = [
    { key: 'question', title: 'Вопрос', render: (r) => r.question || '—' },
    { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'cycle', title: 'Цикл', render: (r) => String(r.cycle ?? '—') },
    { key: 'assignee', title: 'Исполнитель', render: (r) => (r.assignee != null ? String(r.assignee) : '—') },
    { key: 'created_at', title: 'Создан', render: (r) => fmtDate(r.created_at) },
  ];

  const srCols: Column<SupplierRequest>[] = [
    { key: 'rq_number', title: 'RQ №', render: (r) => r.rq_number || shortId(r.id) },
    { key: 'supplier', title: 'Поставщик', render: (r) => shortId(r.supplier) },
    { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'sent_at', title: 'Отправлен', render: (r) => fmtDate(r.sent_at) },
    { key: 'response_due_at', title: 'Ответ до', render: (r) => fmtDate(r.response_due_at) },
    {
      key: 'actions', title: '', render: (r) => (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          {r.status !== 'sent' && r.status !== 'answered' && (
            <ActionButton title="Отмечено отправленным" confirm="Подтвердить внешнюю отправку запроса поставщику?"
              run={() => sourcing.supplierRequestAction(r.id, 'mark-sent', { channel: 'manual', revision: r.revision, sent_at: new Date().toISOString(), confirmed: true })}
              onDone={onChanged} />
          )}
          {r.status === 'sent' && <RegisterResponseButton sr={r} onDone={onChanged} />}
          <ActionButton title="Котировка" run={async () => setQuoteFormFor(r.id)} onDone={() => undefined} />
        </span>
      ),
    },
  ];

  // Анонимизированная проекция котировки: менеджеру показывается anonymous_code,
  // а не реальный поставщик/источник. Если backend вернёт supplier — он не отображается.
  const quoteCols: Column<SupplierQuote>[] = [
    { key: 'anonymous_code', title: 'Код', render: (r) => r.anonymous_code || shortId(r.id) },
    { key: 'version', title: 'Версия', render: (r) => String(r.version ?? '—') },
    { key: 'cost', title: 'Стоимость', render: (r) => fmtMoney(r.cost, r.currency) },
    { key: 'duration', title: 'Срок', render: (r) => r.duration != null ? `${r.duration} ${r.duration_unit === 'days' ? 'дн.' : (r.duration_unit ?? '')}` : '—' },
    { key: 'valid_until', title: 'Действ. до', render: (r) => fmtDate(r.valid_until) },
    { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.published_at ? (r.status ?? 'confirmed') : (r.status ?? 'draft')} /> },
    {
      key: 'actions', title: '', render: (q) => (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
          {!q.published_at && q.status !== 'confirmed' && (
            <ActionButton title="Опубликовать" confirm="Опубликовать котировку менеджеру (анонимно)?"
              run={() => sourcing.publishQuote(q.id, q.revision)} onDone={onChanged} />
          )}
          {(q.published_at || q.status === 'confirmed') && item && (
            <ActionButton title="Выбрать для позиции"
              confirm={`Выбрать котировку ${q.anonymous_code ?? shortId(q.id)} для позиции «${item.title}»?`}
              run={() => sourcing.selectQuote(item.id, q.id, item.revision)} onDone={onChanged} />
          )}
        </span>
      ),
    },
  ];

  return (
    <Card title={`Вариант: ${[option.country, option.scheme, option.series_or_batch].filter(Boolean).join(' · ') || shortId(option.id)}`}
          actions={<StatusBadge value={option.status} />}>
      {item && <div className="muted" style={{ marginBottom: 8 }}>Позиция: {item.title}</div>}

      <div className="section">
        <h3>Техническая квалификация</h3>
        <DataTable columns={caseCols} rows={optionCases} empty="Кейсов нет" />
      </div>

      <div className="section">
        <h3>Запросы поставщикам</h3>
        <DataTable columns={srCols} rows={optionSRs} empty="Запросов нет" />
        <button className="btn small" onClick={() => setShowSRForm((v) => !v)} style={{ marginTop: 8 }}>
          {showSRForm ? 'Скрыть' : 'Новый запрос поставщику'}
        </button>
        {showSRForm && <CreateSupplierRequestForm option={option} onDone={() => { setShowSRForm(false); onChanged(); }} />}
        {quoteFormFor && <CreateQuoteForm sr={optionSRs.find((s) => s.id === quoteFormFor)!} onDone={() => { setQuoteFormFor(null); onChanged(); }} />}
      </div>

      <div className="section">
        <h3>Котировки (анонимизированные)</h3>
        {optionQuotes.length === 0 ? <Empty text="Котировок нет" /> : <DataTable columns={quoteCols} rows={optionQuotes} />}
      </div>

      {option.conditions && Object.keys(option.conditions).length > 0 && (
        <div className="section">
          <h3>Условия варианта</h3>
          <JsonBlock data={option.conditions} />
        </div>
      )}
    </Card>
  );
}

export function CalculationsTab({ data }: { data: CardData }) {
  const [tick, setTick] = useState(0);
  const calc = useCalcData(data.options, tick);
  const reloadAll = () => { setTick((t) => t + 1); data.reload(); };

  if (data.options.length === 0) {
    return <Card><Empty text="Сначала добавьте варианты исполнения на вкладке «Позиции и варианты»" /></Card>;
  }

  return (
    <div>
      <ErrorBox error={calc.error} onRetry={calc.reload} />
      {data.options.map((option) => (
        <OptionCalcBlock
          key={option.id}
          option={option}
          item={data.items.find((i) => i.id === option.item)}
          calc={calc.data ?? { cases: [], supplierRequests: [], quotes: [] }}
          onChanged={reloadAll}
        />
      ))}
    </div>
  );
}
